/**
 * /dashboard/settings/workspace — Settings → Organization → Workspace.
 *
 * Tenant-level configuration. Today carries Professional Tax state
 * (drives G5 payroll); v1.5 will add more.
 *
 * HR-admin only — backend RBAC on /api/v1/tenant/settings is the
 * boundary; the role gate below is the UX mirror.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port
 * of frontend/src/app/(dashboard)/dashboard/settings/workspace/page.tsx
 * folded into a single route that wraps the body in <SettingsShell>
 * (the SettingsLayout from the Next.js group is materialised in every
 * leaf here, since TanStack Router has no group-layout primitive that
 * a child route can opt into without a dot-prefixed layout file).
 */

import { createFileRoute } from "@tanstack/react-router";
import { Settings2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import type { Role } from "@/types/auth";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { WorkspaceSettingsForm } from "@/components/settings/workspace-settings-form";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/workspace",
)({
  component: SettingsWorkspaceRoute,
});

const CAN_MANAGE_WORKSPACE = new Set<Role>([
  "company_admin",
  "hr_manager",
]);

function SettingsWorkspaceRoute() {
  const { user } = useAuth();
  const allowed = !!user?.role && CAN_MANAGE_WORKSPACE.has(user.role);

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        {!allowed ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-muted-foreground">
              Workspace settings are managed by HR admins.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold">Workspace</h2>
              <p className="text-sm text-muted-foreground">
                Company-level configuration that affects every employee.
              </p>
            </div>
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Settings2 aria-hidden className="h-4 w-4 text-primary" />
                  Workspace configuration
                </CardTitle>
                <CardDescription>
                  Settings here apply company-wide; most affect the
                  payroll CSV generator (G5).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <WorkspaceSettingsForm />
              </CardContent>
            </Card>
          </div>
        )}
      </SettingsShell>
    </>
  );
}
